import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);

  const article = useMemo(
    () => articles.find((a) => a.id === articleId),
    [articleId]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter(
        (a) =>
          a.id !== article.id &&
          (a.categorySlug === article.categorySlug ||
            a.tags.some((t) => article.tags.includes(t)))
      )
      .slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pt-6">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: "文章不存在" },
          ]}
        />
        <div className="text-center py-32">
          <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
          </span>
          <h1 className="font-heading text-2xl text-foreground-900 mb-2">
            文章未找到
          </h1>
          <p className="text-foreground-500 mb-6">
            该文章可能已被删除或链接无效
          </p>
          <Link
            to="/"
            className="px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    image: article.coverImage,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.updateDate,
    publisher: {
      "@type": "Organization",
      name: "打屁股漫画精选合集",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://dda668.cn/article/${article.id}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(articleJsonLd),
        }}
      />

      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pt-6">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <article className="py-8 md:py-12 px-4 md:px-6">
        <div className="w-full max-w-4xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {article.category}
              </Link>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-500 rounded-full text-xs whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 text-sm text-foreground-500">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </span>
                {article.author}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </span>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                {article.readTime} 分钟阅读
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </span>
                {article.views.toLocaleString()} 次阅读
              </span>
            </div>
          </div>

          <div className="w-full aspect-[16/9] rounded-lg overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 打屁股漫画精选合集`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose prose-sm md:prose-base max-w-none">
            <div className="bg-background-100 rounded-lg p-6 md:p-8 mb-8">
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                <strong>简介：</strong>
                {article.description}
              </p>
            </div>

            <div className="space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base">
              <p>
                {article.content}
                这是一个关于成长、欢笑与温暖的故事。在平凡的日子里，角色们经历着各种各样的冒险与挑战，每一次的"惩罚"都成为了他们成长路上的宝贵经历。
              </p>
              <p>
                漫画的画风细腻生动，角色表情丰富，让读者能够深入感受每一个情节的起伏。无论是轻松搞笑的日常还是温情脉脉的瞬间，都能让人忍俊不禁或感动落泪。
              </p>
              <p>
                作者通过细腻的笔触和巧妙的情节设计，将看似简单的"打屁股"主题演绎得丰富多彩。每一篇漫画都蕴含着独特的情感内核——友情、亲情、成长、理解与包容。
              </p>
              <p>
                如果你是第一次接触这类型的漫画，你一定会被它独特的魅力所吸引。如果你已经是老读者，那么每一篇新作都会给你带来全新的惊喜和感动。
              </p>
              <p>
                让我们一起沉浸在这个充满创意与温情的漫画世界里，感受每一个角色的喜怒哀乐，体验每一段独特的故事情节。收藏本站，不错过任何一篇精彩内容！
              </p>
            </div>

            <div className="mt-8 p-6 bg-background-100 rounded-lg">
              <p className="text-sm text-foreground-500 leading-relaxed">
                <strong>最后更新：</strong>
                {article.updateDate}
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <strong>分类：</strong>
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-primary-500 hover:text-primary-600 transition-colors"
                >
                  {article.category}
                </Link>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <strong>标签：</strong>
                {article.tags.join("、")}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-background-200/70">
            <Link
              to={`/category/${article.categorySlug}`}
              className="flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              返回{article.category}分类
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="w-full max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-8 text-center">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {relatedArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}